import { TypedDocumentNode, gql } from '@apollo/client';
import { faCheck, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '__generated__/globalTypes';
import Button from 'atoms/buttons/Button';
import IconButton from 'atoms/buttons/IconButton';
import { FontAwesomeIcon } from 'atoms/icons';
import { Drawer } from 'atoms/layout/Drawer';
import { ScarcityBackground } from 'atoms/layout/ScarcityBackground';
import { Horizontal, SBHorizontal, Vertical } from 'atoms/layout/flex';
import { Tooltip } from 'atoms/tooltip/Tooltip';
import { BodyL, BodyM, BodyS, HeadlineXS, Text14 } from 'atoms/typography';
import CardBack from 'components/card/Back';
import Card from 'components/card/Card';
import OpenItemDialogLink from 'components/link/OpenItemDialogLink';
import { ClaimReferralRewardDialog } from 'components/referral/ClaimReferralRewardDialog';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useReferralReward } from 'hooks/useReferralReward';
import { scarcityNames } from 'lib/cards';
import { glossary } from 'lib/glossary';
import { qualityNames } from 'lib/players';
import { desktopAndAbove } from 'style/mediaQuery';

import { Milestones_referralMilestoneReward } from './__generated__/index.graphql';

const List = styled(Vertical).attrs({ gap: 0 })`
  &.flat {
    flex-direction: row;
  }
  @media ${desktopAndAbove} {
    flex-direction: row;
  }
`;

const MilestoneRoot = styled(Horizontal).attrs({ gap: 2 })`
  --padding: var(--double-unit);
  --circle-width: var(--double-unit);
  --path-width: var(--half-unit);

  padding: var(--padding);
  position: relative;

  &:before,
  &::after {
    content: '';
    position: absolute;
    z-index: 0;
    background-color: var(--c-neutral-400);
    width: var(--path-width);
    height: 50%;
    left: calc(var(--padding) + (var(--circle-width) - var(--path-width)) / 2);
  }
  &:before {
    top: 0;
  }
  &:after {
    bottom: 0;
  }
  @media ${desktopAndAbove} {
    flex-grow: 1;
    flex-direction: column-reverse;
    &::before,
    &::after {
      top: auto;
      bottom: calc(
        var(--padding) + (var(--circle-width) - var(--path-width)) / 2
      );
      height: var(--path-width);
      width: 50%;
      left: auto;
    }
    &:before {
      left: 0;
    }
    &:after {
      right: 0;
    }
  }

  &.reached:before {
    background-color: var(--c-static-green-600);
  }

  &.reachedAfter:after {
    background-color: var(--c-static-green-600);
  }

  &.first:before,
  &.last:after {
    display: none;
  }

  &.flat {
    flex-grow: 1;
    flex-direction: column-reverse;
    &::before,
    &:after {
      top: auto;
      bottom: calc(
        var(--padding) + (var(--circle-width) - var(--path-width)) / 2
      );
      height: var(--path-width);
      width: 50%;
      left: auto;
    }
    &:before {
      left: 0;
    }
    &:after {
      right: 0;
    }
  }
`;

const Content = styled(SBHorizontal)`
  flex: 1;
  &.flat {
    flex-direction: column;
  }
  @media ${desktopAndAbove} {
    flex-direction: column;
  }
`;

const DrawerContent = styled(Vertical)`
  background: var(--c-neutral-100);
  padding: var(--double-unit);
`;

const Details = styled(Horizontal).attrs({ gap: 2 })`
  @media ${desktopAndAbove} {
    flex-direction: column;
  }
`;
const Circle = styled(ScarcityBackground)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--circle-width);
  height: var(--circle-width);
  background-color: var(--c-neutral-100);
  border: 3px solid var(--c-neutral-400);
  border-radius: 100%;
  position: relative;
  z-index: 1;
  .reached & {
    background-color: var(--c-static-green-600);
    border: none;
  }
`;
const CardWrapper = styled.div`
  width: 40px;
`;
const Completed = styled(Text14)`
  margin-left: auto;
`;

const Milestone = ({
  first,
  last,
  milestone: {
    referralNumber,
    rewardRarity,
    rewardTier,
    reward,
    reached,
    reachedAfter,
  },
  sport,
  hideCards,
}: {
  first: boolean;
  last: boolean;
  milestone: Milestones_referralMilestoneReward & {
    reached?: boolean;
    reachedAfter?: boolean;
  };
  sport: Sport;
  hideCards?: boolean;
}) => {
  const { currentUser } = useCurrentUserContext();
  const { claimed } = useReferralReward(reward);
  const [openClaimDialog, setOpenClaimDialog] = useState(false);
  if (!currentUser) return null;

  const milestoneDetail = (
    <Content>
      <Details>
        <CardWrapper>
          {reward?.card && claimed ? (
            <OpenItemDialogLink sport={reward.card.sport} item={reward.card}>
              <Card card={reward.card} />
            </OpenItemDialogLink>
          ) : (
            <CardBack
              rarity={rewardRarity}
              tier={rewardTier ? +rewardTier : undefined}
            />
          )}
        </CardWrapper>
        <Vertical center={hideCards} gap={0}>
          <BodyS as="p" bold>
            <FormattedMessage
              id="Notice.MilestonesNotice.Milestones.Milestone.referrals2"
              defaultMessage="{count, plural, one {# Successful Referral} other {# Successful Referrals}}"
              values={{ count: referralNumber }}
            />
          </BodyS>
          <Horizontal gap={0.5}>
            <BodyS as="p">{scarcityNames[rewardRarity]}</BodyS>
            {rewardTier && (
              <BodyS as="p" color="var(--c-brand-300)">
                {qualityNames[rewardTier]}
              </BodyS>
            )}
          </Horizontal>
        </Vertical>
      </Details>

      {claimed && (
        <Completed bold color="var(--c-green-800)">
          <FormattedMessage {...glossary.completed} />
        </Completed>
      )}
      {reward && !claimed && (
        <Button
          size="compact"
          color="secondary"
          onClick={e => {
            e.preventDefault();
            setOpenClaimDialog(true);
          }}
        >
          <FormattedMessage {...glossary.claim} />
        </Button>
      )}
    </Content>
  );
  return (
    <MilestoneRoot
      className={classNames({
        first,
        last,
        reached,
        reachedAfter,
        flat: hideCards,
      })}
    >
      <Tooltip title={hideCards ? milestoneDetail : ''}>
        <button type="button">
          <Circle className={rewardRarity}>
            <>{reached && <FontAwesomeIcon icon={faCheck} width={10} />}</>
          </Circle>
        </button>
      </Tooltip>
      {!hideCards && milestoneDetail}
      <ClaimReferralRewardDialog
        referralReward={reward}
        open={openClaimDialog}
        onClose={() => setOpenClaimDialog(false)}
        sport={sport}
      />
    </MilestoneRoot>
  );
};

type Props = {
  currentProgression: number;
  milestones?: Milestones_referralMilestoneReward[];
  sport: Sport;
  hideCards?: boolean;
};

export const Milestones = ({
  milestones,
  currentProgression,
  sport,
  hideCards,
}: Props) => {
  const [openDetails, setOpenDetails] = useState(false);

  const sortedMilestones = useMemo(
    () =>
      milestones?.slice().sort((a, b) => a.referralNumber - b.referralNumber) ||
      new Array(5).fill({}),
    [milestones]
  );

  const preparedMilestones: (Milestones_referralMilestoneReward & {
    reached?: boolean;
    reachedAfter?: boolean;
  })[] = useMemo(
    () =>
      sortedMilestones.map(milestone => {
        if (currentProgression === milestone.referralNumber) {
          return {
            ...milestone,
            reached: true,
          };
        }

        return {
          ...milestone,
          reached: currentProgression > milestone.referralNumber,
          reachedAfter: currentProgression > milestone.referralNumber,
        };
      }),
    [currentProgression, sortedMilestones]
  );

  return (
    <Vertical gap={2}>
      <Horizontal gap={0.5}>
        <HeadlineXS as="h4">
          <FormattedMessage
            id="FootballNotice.forEachMilestone"
            defaultMessage="Milestones"
          />
          {' - '}
        </HeadlineXS>
        <BodyM as="p" color="var(--c-neutral-600)">
          <FormattedMessage
            id="Notice.Milestones.outOfCompleted"
            defaultMessage="{reached} out of {total} completed"
            values={{
              reached: preparedMilestones.filter(m => m.reached).length,
              total: preparedMilestones.length,
            }}
          />
        </BodyM>
        {hideCards && (
          <IconButton
            icon={faInfoCircle}
            color="transparent"
            smaller
            onClick={() => setOpenDetails(true)}
          />
        )}
      </Horizontal>
      <List className={classNames({ flat: hideCards })}>
        {preparedMilestones.map((milestone, i) => (
          <Milestone
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            first={!i}
            last={i === preparedMilestones.length - 1}
            milestone={milestone}
            sport={sport}
            hideCards={hideCards}
          />
        ))}
      </List>
      <Drawer
        side="bottom"
        open={openDetails}
        onBackdropClick={() => setOpenDetails(false)}
      >
        <DrawerContent>
          <Vertical>
            <BodyL as="h2">
              <FormattedMessage
                id="Milestones.details.title"
                defaultMessage="Milestones"
              />
            </BodyL>
            <BodyM as="p" color="var(--c-neutral-600)">
              <FormattedMessage
                id="Milestones.details.description"
                defaultMessage="The more people you invite, the better the rewards"
              />
            </BodyM>
          </Vertical>
          <List>
            {preparedMilestones.map((milestone, i) => (
              <Milestone
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                first={!i}
                last={i === preparedMilestones.length - 1}
                milestone={milestone}
                sport={sport}
              />
            ))}
          </List>
        </DrawerContent>
      </Drawer>
    </Vertical>
  );
};

Milestones.fragments = {
  ReferralMilestoneReward: gql`
    fragment Milestones_referralMilestoneReward on ReferralMilestoneReward {
      referralNumber
      rewardRarity
      rewardTier
      reward {
        id
        card: anyCard {
          slug
          sport
          ...Card_anyCard
        }
        ...ClaimReferralRewardDialog_referralReward
        ...useReferralReward_referralReward
      }
    }
    ${ClaimReferralRewardDialog.fragments.referralReward}
    ${useReferralReward.fragments.referralReward}
    ${Card.fragments.anyCard}
  ` as TypedDocumentNode<Milestones_referralMilestoneReward>,
};
